// ===========================================
// Data Table Component
// ===========================================
// Reusable table component with sorting, filtering, and actions

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Collapse,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';

// ===========================================
// TYPES
// ===========================================

export interface Column<T> {
  id: keyof T | string;
  label: string;
  sortable?: boolean;
  /** Optional path used for sorting when id doesn't map to a sortable value */
  sortKey?: string;
  /** Optional function to extract a sort value from a row */
  sortValue?: (row: T) => string | number | boolean | null | undefined;
  /** Optional function to extract a search value from a row (overrides id-based lookup) */
  searchValue?: (row: T) => string;
  /** Secondary label shown below main label in header, with its own sort */
  subLabel?: string;
  /** Sort value function for the sub label */
  subSortValue?: (row: T) => string | number | boolean | null | undefined;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  width?: number;
  format?: (value: unknown, row: T) => React.ReactNode;
}

export interface ExpandableConfig<T> {
  /** Content to render inside the expanded row */
  renderContent: (row: T) => React.ReactNode;
  /** Predicate to hide the chevron when a row has nothing to expand */
  isExpandable?: (row: T) => boolean;
}

export interface DataTableProps<T extends { id: string }> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onView?: (row: T) => void;
  emptyMessage?: string;
  /** Function to determine if delete is allowed for a row. Returns true if delete is allowed. */
  canDelete?: (row: T) => boolean;
  /** Tooltip message when delete is disabled */
  deleteDisabledTooltip?: string;
  /** Render additional action buttons for each row */
  renderExtraActions?: (row: T) => React.ReactNode;
  /** Enable pagination (default: true) */
  paginated?: boolean;
  /** Options for rows per page selector (default: [10, 25, 50]) */
  rowsPerPageOptions?: number[];
  /** Default rows per page (default: 25) */
  defaultRowsPerPage?: number;
  /** Opt-in: render an expandable detail row under each main row */
  expandable?: ExpandableConfig<T>;
  /** Opt-in: below the md breakpoint, column cells never wrap (the table already scrolls sideways there) */
  noWrapOnMobile?: boolean;
}

type Order = 'asc' | 'desc';

// ===========================================
// COMPONENT
// ===========================================

export default function DataTable<T extends { id: string }>({
  columns,
  data,
  loading = false,
  searchable = true,
  searchPlaceholder = 'Search...',
  onEdit,
  onDelete,
  onView,
  emptyMessage = 'No data found',
  canDelete,
  deleteDisabledTooltip = 'Cannot delete this item',
  renderExtraActions,
  paginated = true,
  rowsPerPageOptions = [10, 25, 50],
  defaultRowsPerPage = 25,
  expandable,
  noWrapOnMobile = false,
}: DataTableProps<T>) {
  const cellWrapSx = noWrapOnMobile ? { whiteSpace: { xs: 'nowrap', md: 'normal' } } : undefined;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Handle sort
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Get nested value from object - memoized to satisfy deps
  const getNestedValue = useCallback((obj: T, path: string): unknown => {
    return path.split('.').reduce((acc: unknown, part) => {
      if (acc && typeof acc === 'object' && part in acc) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj);
  }, []);

  // Reset page when search query changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const value = col.searchValue
            ? col.searchValue(row)
            : getNestedValue(row, col.id as string);
          return value?.toString().toLowerCase().includes(query);
        })
      );
    }

    // Sort
    if (orderBy) {
      const isSubSort = orderBy.endsWith('__sub__');
      const colId = isSubSort ? orderBy.replace('__sub__', '') : orderBy;
      const col = columns.find((c) => (c.id as string) === colId);
      result.sort((a, b) => {
        const aValue = isSubSort && col?.subSortValue ? col.subSortValue(a)
          : col?.sortValue ? col.sortValue(a)
          : getNestedValue(a, col?.sortKey || colId);
        const bValue = isSubSort && col?.subSortValue ? col.subSortValue(b)
          : col?.sortValue ? col.sortValue(b)
          : getNestedValue(b, col?.sortKey || colId);

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
          comparison = aValue === bValue ? 0 : aValue ? -1 : 1;
        }

        return order === 'desc' ? -comparison : comparison;
      });
    }

    return result;
  }, [data, searchQuery, orderBy, order, columns, getNestedValue]);

  // Paginate filtered data
  const processedData = useMemo(() => {
    if (!paginated) return filteredData;
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, paginated, page, rowsPerPage]);

  // Check if there are actions
  const hasActions = onEdit || onDelete || onView || renderExtraActions;
  const hasExpand = Boolean(expandable);
  const totalColumns = columns.length + (hasExpand ? 1 : 0) + (hasActions ? 1 : 0);

  // Check if delete is allowed for a row
  const isDeleteAllowed = (row: T) => !canDelete || canDelete(row);

  // Render loading skeleton
  if (loading) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {searchable && (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={40} />
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {hasExpand && (
                  <TableCell style={{ width: 48 }}>
                    <Skeleton variant="text" />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.id as string}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {hasExpand && (
                    <TableCell>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.id as string}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Search Bar */}
      {searchable && (
        <Box sx={{ p: 2 }}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {hasExpand && (
                <TableCell style={{ width: 48, padding: '0 8px' }} />
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id as string}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth, width: column.width, verticalAlign: 'top' }}
                  sx={cellWrapSx}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === (column.id as string)}
                      direction={orderBy === (column.id as string) ? order : 'asc'}
                      onClick={() => handleSort(column.id as string)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                  {column.subLabel && (
                    <Box component="div" sx={{ mt: 0.25 }}>
                      {column.subSortValue ? (
                        <TableSortLabel
                          active={orderBy === `${column.id as string}__sub__`}
                          direction={orderBy === `${column.id as string}__sub__` ? order : 'asc'}
                          onClick={() => handleSort(`${column.id as string}__sub__`)}
                          sx={{ fontSize: '0.7rem', color: 'text.secondary', '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                        >
                          {column.subLabel}
                        </TableSortLabel>
                      ) : (
                        <Typography variant="caption" color="text.secondary">{column.subLabel}</Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell align="right" style={{ minWidth: 100, width: 100, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={totalColumns}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              processedData.flatMap((row) => {
                const rowCanExpand =
                  hasExpand && (expandable!.isExpandable ? expandable!.isExpandable(row) : true);
                const isExpanded = rowCanExpand && expandedIds.has(row.id);

                const mainRow = (
                <TableRow hover key={row.id}>
                  {hasExpand && (
                    <TableCell style={{ width: 48, padding: '0 8px' }}>
                      {rowCanExpand ? (
                        <IconButton
                          size="small"
                          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          aria-expanded={isExpanded}
                          onClick={() => toggleExpanded(row.id)}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </TableCell>
                  )}
                  {columns.map((column) => {
                    const value = getNestedValue(row, column.id as string);
                    return (
                      <TableCell key={column.id as string} align={column.align || 'left'} sx={cellWrapSx}>
                        {column.format
                          ? column.format(value, row)
                          : typeof value === 'boolean'
                          ? value ? (
                              <Chip label="Active" size="small" color="success" />
                            ) : (
                              <Chip label="Inactive" size="small" color="default" />
                            )
                          : (value as React.ReactNode) ?? '-'}
                      </TableCell>
                    );
                  })}
                  {hasActions && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {renderExtraActions && renderExtraActions(row)}
                      {onView && (
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => onView(row)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEdit && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDelete && (
                        <Tooltip title={isDeleteAllowed(row) ? 'Delete' : deleteDisabledTooltip}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => isDeleteAllowed(row) && onDelete(row)}
                              color={isDeleteAllowed(row) ? 'error' : 'default'}
                              disabled={!isDeleteAllowed(row)}
                              sx={{
                                opacity: isDeleteAllowed(row) ? 1 : 0.5,
                                cursor: isDeleteAllowed(row) ? 'pointer' : 'not-allowed',
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                );

                if (!hasExpand) return [mainRow];

                const expansionRow = (
                  <TableRow key={`${row.id}-expansion`}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1.5, pl: 6, pr: 2 }}>
                          {expandable!.renderContent(row)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                );

                return [mainRow, expansionRow];
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginated && filteredData.length > 0 && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </Paper>
  );
}
